import { useState, useEffect, useRef, useMemo } from "react";
import Button from "@mui/material/Button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useParams } from "react-router";
import CircularProgress from "@mui/material/CircularProgress";
import EnhancedTable from "components/utils/Table.js";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import EnrollmentService from "services/EnrollmentService.js";
import Chip from "@mui/material/Chip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { Outlet } from "react-router-dom";
import TableRowActionsCell from "components/utils/TableRowActionsCell.js";
import PopUpModal from "components/utils/PopUpModal.js";
import AuditFieldsModal from "components/modals/AuditFieldsModal.js";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import useAPI from "hooks/useAPI.js";
import DateUtils from "utils/DateUtils.js";
import { useOutletContext } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

const SESSION_TYPE_BY_TAB = {
  academic: "Academic",
  therapy: "Therapy",
  neurofeedback: "Neurofeedback",
};

const standardTableHeadings = [
  { id: "index", label: "S.No." },
  { id: "type", label: "Type" },
  { id: "status", label: "Status" },
  { id: "createdBy", label: "Created By" },
  { id: "lastUpdatedBy", label: "Last Updated By" },
  { id: "actions", label: "Actions" },
];

const lensTableHeadings = [
  { id: "index", label: "S.No." },
  { id: "sessionNumber", label: "Session #", align: "center" },
  { id: "type", label: "Type" },
  { id: "status", label: "Status" },
  { id: "createdBy", label: "Created By" },
  { id: "lastUpdatedBy", label: "Last Updated By" },
  { id: "actions", label: "Actions" },
];

export function EnrollmentSessions() {
  const [hospitalId] = useOutletContext();
  return <Outlet context={[hospitalId]} />;
}

export function EnrollmentSessionList() {
  const navigate = useNavigate();
  const [hospitalId] = useOutletContext();
  const { patientId, enrollmentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(searchParams.get("tab") || "all");
  const [enrollmentType, setEnrollmentType] = useState("STANDARD");
  const [rawSessions, setRawSessions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    total: 0,
  });
  const [auditFieldModalContent, setAuditFieldModalContent] = useState({
    createdAt: "",
    lastUpdatedAt: "",
    createdBy: "",
    lastUpdatedBy: "",
  });
  const auditFieldModalRef = useRef(null);
  const deleteSessionModalRef = useRef(null);
  const [deletingSessionId, setDeletingSessionId] = useState("");

  const isLensEnrollment = enrollmentType === "LENS_NFB";
  const tableHeadings = isLensEnrollment ? lensTableHeadings : standardTableHeadings;
  const sessionFormType =
    isLensEnrollment || tabValue === "all" ? "" : SESSION_TYPE_BY_TAB[tabValue] || "";

  const getEnrollmentAPI = useAPI(EnrollmentService.getEnrollment, (response) => {
    setEnrollmentType(response.data.enrollmentType);
  });

  const getSessionsAPI = useAPI(EnrollmentService.getEnrollmentSessions, (response) => {
    const { content, totalElements } = response.data;
    setRawSessions(content);
    setPagination((prev) => ({ ...prev, total: totalElements }));
  });

  function fetchSessions(page = pagination.page, size = pagination.size) {
    getSessionsAPI.execute(hospitalId, patientId, enrollmentId, page, size, sessionFormType);
  }

  const addSessionAPI = useAPI(EnrollmentService.createEnrollmentSession, () => {
    setPagination((prev) => {
      const lastPage = Math.max(0, Math.ceil((prev.total + 1) / prev.size) - 1);
      if (lastPage === prev.page) {
        fetchSessions(lastPage, prev.size);
      }
      return { ...prev, page: lastPage };
    });
  });

  const deleteSessionAPI = useAPI(
    EnrollmentService.deleteEnrollmentSession,
    () => fetchSessions(),
    true
  );
  const revokeSessionSubmissionAPI = useAPI(
    EnrollmentService.revokeEnrollmentSessionSubmission,
    () => fetchSessions(),
    true
  );

  useEffect(() => {
    getEnrollmentAPI.execute(hospitalId, patientId, enrollmentId);
  }, [hospitalId, patientId, enrollmentId]);

  useEffect(() => {
    if (hospitalId && patientId && enrollmentId) {
      fetchSessions();
    }
  }, [hospitalId, patientId, enrollmentId, pagination.page, pagination.size, sessionFormType]);

  function ActionsCell({ session }) {
    const actions = {
      startItems: [
        {
          component: <InfoOutlinedIcon fontSize="inherit" color="custom.light" />,
          onClickHandler: () => {
            setAuditFieldModalContent({
              createdAt: DateUtils.formatDateTime(session.createdAt),
              lastUpdatedAt: DateUtils.formatDateTime(session.lastUpdatedAt),
              createdBy: session.createdByUserFullName,
              lastUpdatedBy: session.lastUpdatedByUserFullName,
            });
            auditFieldModalRef.current.openModal();
          },
        },
        {
          component: <DeleteOutlineIcon fontSize="inherit" color="error" />,
          onClickHandler: () => {
            setDeletingSessionId(session.id);
            deleteSessionModalRef.current.openModal();
          },
        },
      ],
      menuItems: session.isCompleted
        ? [{
            label: "Revoke Submission",
            onClickHandler: () =>
              revokeSessionSubmissionAPI.execute(hospitalId, patientId, enrollmentId, session.id),
          }]
        : [],
    };

    return <TableRowActionsCell actions={actions} />;
  }

  const tableSessions = useMemo(
    () =>
      rawSessions.map((session, index) => {
        const isGeneratedFromStimForm = session.metadata?.generated_from_stim_form === true;

        return {
          id: session.id,
          index: pagination.page * pagination.size + index + 1,
          ...(isLensEnrollment ? { sessionNumber: session.sessionNumber || "—" } : {}),
          type: (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {session.type}
              {isGeneratedFromStimForm && (
                <Chip label="Stim Form" size="small" color="info" variant="outlined" style={{ fontSize: "0.75rem" }} />
              )}
            </div>
          ),
          status: getStatusChip(session.isCompleted),
          createdBy: session.createdByUserFullName,
          lastUpdatedBy: session.lastUpdatedByUserFullName,
          actions: <ActionsCell key={session.id} session={session} />,
          documentLink: true,
          sessionType: session.type,
          isCompleted: session.isCompleted,
        };
      }),
    [rawSessions, isLensEnrollment, pagination.page, pagination.size]
  );

  function addSession(sessionType) {
    addSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionType);
  }

  function handleRowClick(row) {
    const { sessionType, id, isCompleted } = row;

    if (sessionType === "Therapy") {
      navigate(`therapy/${id}?isCompleted=${isCompleted}`);
    } else if (sessionType === "Academic") {
      navigate(`academic/${id}?isCompleted=${isCompleted}`);
    } else if (sessionType === "Neurofeedback") {
      navigate(`neurofeedback/${id}?isCompleted=${isCompleted}`);
    } else if (sessionType === "LENS Neurofeedback") {
      navigate(`lens-nfb/${id}?isCompleted=${isCompleted}`);
    }
  }

  function handleTabChange(event, newValue) {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setTabValue(newValue);
    setSearchParams({ tab: newValue }, { replace: true });
  }

  function renderAddButton(label, sessionType, color) {
    if (addSessionAPI.status === "pending") {
      return (
        <LoadingButton
          loading
          loadingPosition="start"
          startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
          variant="contained"
          color={color}
        >
          {label}
        </LoadingButton>
      );
    }

    return (
      <Button
        onClick={() => addSession(sessionType)}
        startIcon={<img src="assets/plus-icon.svg" alt="Add icon" />}
        variant="contained"
        color={color}
      >
        {label}
      </Button>
    );
  }

  return (
    <Paper elevation={0} className="surface-card section-card app-card p-4">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button onClick={() => navigate(-1)} variant="outlined">
          Back
        </Button>
        <Stack direction="row" spacing={2}>
          {!isLensEnrollment && (
            <>
              {renderAddButton("Therapy", "THERAPY")}
              {renderAddButton("Academic", "ACADEMIC")}
              {renderAddButton("Neurofeedback", "NEUROFEEDBACK")}
            </>
          )}
          {isLensEnrollment && renderAddButton("LENS NFB", "LENS_NFB", "info")}
        </Stack>
      </Stack>

      {!isLensEnrollment && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 3, mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="session types">
            <Tab label="All Sessions" value="all" />
            <Tab label="Academics" value="academic" />
            <Tab label="Therapy" value="therapy" />
            <Tab label="Neurofeedback" value="neurofeedback" />
          </Tabs>
        </Box>
      )}

      <Box sx={{ mt: isLensEnrollment ? 3 : 0 }}>
        {getSessionsAPI.status === "pending" ? (
          <div className="loading-state-shell text-center">
            <CircularProgress className="text-center" sx={{ my: 2 }} />
          </div>
        ) : (
          <EnhancedTable
            headings={tableHeadings}
            rowsData={tableSessions}
            rowsPerPage={pagination.size}
            page={pagination.page}
            count={pagination.total}
            serverSide
            onPageChange={(nextPage) => {
              setPagination((prev) => ({ ...prev, page: nextPage }));
            }}
            onRowsPerPageChange={(nextSize) => {
              setPagination((prev) => ({ ...prev, size: nextSize, page: 0 }));
            }}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      <PopUpModal ref={auditFieldModalRef} modalWidth={320}>
        <AuditFieldsModal data={auditFieldModalContent} />
      </PopUpModal>
      <PopUpModal ref={deleteSessionModalRef} modalWidth={400}>
        <ConfirmActionModal
          content="Do you want to confirm deleting the session?"
          confirmActionHandler={() => {
            deleteSessionAPI.execute(hospitalId, patientId, enrollmentId, deletingSessionId);
            deleteSessionModalRef.current.closeModal();
          }}
          onCloseHandler={() => deleteSessionModalRef.current.closeModal()}
        />
      </PopUpModal>
    </Paper>
  );
}

function getStatusChip(isCompleted) {
  if (isCompleted) {
    return <Chip label="Completed" color="success" size="small" className="app-status-badge app-status-success" />;
  }

  return <Chip label="Incomplete" color="warning" size="small" className="app-status-badge app-status-warning" />;
}
